import React from 'react';
import { PacmanGameState } from '../PacmanGame';

interface GameStateOverlayProps {
    gameState: PacmanGameState;
    gameStarted: boolean;
    score: number;
    level: number;
    onStartGame: () => void;
    onResetGame: () => void;
    onStartNextLevel: () => void;
}

const GameStateOverlay: React.FC<GameStateOverlayProps> = ({
    gameState,
    gameStarted,
    score,
    level,
    onStartGame,
    onResetGame,
    onStartNextLevel
}) => {
    if (gameStarted && gameState === PacmanGameState.Playing) {
        return null; // No overlay when playing
    }

    const getOverlayContent = () => {
        if (!gameStarted) {
            return {
                title: '🚀 SPACE DOTMAN 🚀',
                subtitle: 'Embark on a cosmic adventure!',
                color: '#00BFFF',
                buttonText: '🚀 START ADVENTURE',
                buttonColor: '#41b9e1',
                onButtonClick: onStartGame,
                icon: '⭐'
            };
        }

        switch (gameState) {
            case PacmanGameState.LevelCompleteScreen:
                return {
                    title: '🌟 LEVEL COMPLETE! 🌟',
                    subtitle: `Cosmic Score: ${score.toLocaleString()} | Galaxy Level: ${level}`,
                    color: '#4169E1',
                    buttonText: `🚀 CONTINUE TO LEVEL ${level}`,
                    buttonColor: '#00BFFF',
                    onButtonClick: onStartNextLevel,
                    icon: '⭐'
                };
            case PacmanGameState.GameOver:
                return {
                    title: '💀 GAME OVER 💀',
                    subtitle: `Final Cosmic Score: ${score.toLocaleString()}`,
                    color: '#9370DB',
                    buttonText: '🔄 RESTART ADVENTURE',
                    buttonColor: '#41b9e1',
                    onButtonClick: onResetGame,
                    icon: '☄️'
                };
            case PacmanGameState.Paused:
                return {
                    title: '⏸️ PAUSED ⏸️',
                    subtitle: 'Take a break in the cosmic nebula',
                    color: '#00BFFF',
                    buttonText: '▶️ RESUME ADVENTURE',
                    buttonColor: '#41b9e1',
                    onButtonClick: onStartGame,
                    icon: '🌌'
                };
            default:
                return null;
        }
    };

    const content = getOverlayContent();
    if (!content) return null;

    return (
        <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            // background: 'linear-gradient(135deg, rgba(25,25,112,0.9) 0%,rgb(65, 105, 225) 50%, rgba(0,191,255,0.7) 100%)',
            background: 'linear-gradient(135deg,rgba(14, 82, 104, 0.46) 0%,rgba(14, 82, 104, 0.46) 50%,rgba(0, 191, 255, 0.46) 100%)',
            padding: '15px',
            borderRadius: '10px',
            border: '2px solid #191970',
            boxShadow: '0 8px 25px rgba(0,0,0,0.5), 0 0 15px rgba(65,105,225,0.3)',
            minWidth: '180px',
            animation: 'overlayGlow 2s ease-in-out infinite alternate'
        }}>
            {/* Header */}
            <div style={{
                color: '#00BFFF',
                fontWeight: 'bold',
                marginBottom: '10px',
                textAlign: 'center',
                fontSize: '16px',
                textShadow: '1px 1px 3px rgba(0,0,0,0.8)'
            }}>
                {content.icon} {content.title} {content.icon}
            </div>

            {/* Subtitle */}
            {content.subtitle && (
                <div style={{
                    color: '#F4E4BC',
                    fontSize: '12px',
                    lineHeight: '1.4',
                    marginBottom: '15px',
                    padding: '5px',
                    background: 'rgba(0, 208, 255, 0.1)',
                    borderRadius: '5px',
                    border: '1px solid rgba(255,215,0,0.3)'
                }}>
                    {content.subtitle}
                </div>
            )}

            {/* Button */}
            <button
                onClick={content.onButtonClick}
                style={{
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    background: `linear-gradient(45deg, ${content.buttonColor}, #0e5168)`,
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                    transition: 'all 0.3s ease',
                    textShadow: '1px 1px 2px rgba(255,255,255,0.3)',
                    width: '100%'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.4)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                }}
            >
                {content.buttonText}
            </button>

            <style>{`
                @keyframes overlayGlow {
                    0% { box-shadow: 0 8px 25px rgba(0,0,0,0.5), 0 0 15px rgba(65,105,225,0.3); }
                    100% { box-shadow: 0 8px 25px rgba(0,0,0,0.5), 0 0 25px rgba(65,105,225,0.5); }
                }
            `}</style>
        </div>
    );
};

export default GameStateOverlay;